import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { TaskUI, TaskStatus } from '@flexiplanner/types';

interface TasksState {
  tasks: TaskUI[];
  selectedTaskId: string | null;
  expandedTaskIds: Set<string>;

  // Actions
  setTasks: (tasks: TaskUI[]) => void;
  addTask: (task: TaskUI) => void;
  updateTask: (id: string, updates: Partial<TaskUI>) => void;
  deleteTask: (id: string) => void;
  selectTask: (id: string | null) => void;
  toggleExpanded: (id: string) => void;

  // Selectors
  getTask: (id: string) => TaskUI | undefined;
  getTasksByParent: (parentId: string | null) => TaskUI[];
  getTasksByStatus: (status: TaskStatus) => TaskUI[];
  getUnscheduledTasks: () => TaskUI[];
  getScheduledTasksForDate: (date: string) => TaskUI[];
}

export const useTasksStore = create<TasksState>()(
  persist(
    (set, get) => ({
      tasks: [],
      selectedTaskId: null,
      expandedTaskIds: new Set(),

      setTasks: tasks => {
        set({ tasks: dedupeTasksById(tasks) });
      },

      addTask: task => {
        set(state => ({
          tasks: upsertTaskById(state.tasks, task),
        }));
      },

      updateTask: (id, updates) => {
        set(state => ({
          tasks: state.tasks.map(task =>
            task.id === id ? { ...task, ...updates, updatedAt: new Date().toISOString() } : task
          ),
        }));
      },

      deleteTask: id => {
        set(state => ({
          tasks: state.tasks.filter(task => task.id !== id),
          selectedTaskId: state.selectedTaskId === id ? null : state.selectedTaskId,
        }));
      },

      selectTask: id => set({ selectedTaskId: id }),

      toggleExpanded: id => {
        set(state => {
          const newExpanded = new Set(state.expandedTaskIds);
          if (newExpanded.has(id)) {
            newExpanded.delete(id);
          } else {
            newExpanded.add(id);
          }
          return { expandedTaskIds: newExpanded };
        });
      },

      getTask: id => get().tasks.find(task => task.id === id),

      getTasksByParent: parentId => {
        return get()
          .tasks.filter(task => task.parentId === parentId)
          .sort((a, b) => a.position - b.position);
      },

      getTasksByStatus: status => {
        return get().tasks.filter(task => task.status === status);
      },

      getUnscheduledTasks: () => {
        return get().tasks.filter(
          task =>
            !task.scheduledStart &&
            !task.scheduledEnd &&
            task.status !== 'done' &&
            task.status !== 'completed' &&
            task.status !== 'cancelled'
        );
      },

      getScheduledTasksForDate: date => {
        return get().tasks.filter(task => {
          if (!task.scheduledStart) return false;
          const d = new Date(task.scheduledStart);
          const localDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          return localDate === date;
        });
      },
    }),
    {
      name: 'flexiplanner-tasks',
      partialize: state => ({
        expandedTaskIds: Array.from(state.expandedTaskIds),
      }),
      merge: (persisted, current) => ({
        ...current,
        expandedTaskIds: new Set((persisted as any)?.expandedTaskIds || []),
      }),
    }
  )
);

function upsertTaskById(tasks: TaskUI[], nextTask: TaskUI): TaskUI[] {
  const existingIndex = tasks.findIndex(task => task.id === nextTask.id);
  if (existingIndex === -1) return [...tasks, nextTask];

  const nextTasks = tasks.filter(task => task.id !== nextTask.id);
  nextTasks.splice(existingIndex, 0, nextTask);
  return nextTasks;
}

function dedupeTasksById(tasks: TaskUI[]): TaskUI[] {
  const seen = new Set<string>();
  const deduped: TaskUI[] = [];

  for (const task of tasks) {
    if (seen.has(task.id)) {
      const existingIndex = deduped.findIndex(existing => existing.id === task.id);
      if (existingIndex !== -1) deduped[existingIndex] = task;
      continue;
    }
    seen.add(task.id);
    deduped.push(task);
  }

  return deduped;
}
